import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, 
  Edit, 
  Save,
  Camera,
  Mail,
  Phone,
  MapPin,
  Calendar,
  BookOpen,
  Award,
  Clock,
  Eye,
  EyeOff,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Shield } from 'lucide-react';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentProfileByToken, getStudentEnrollments, createOrUpdateStudentProfile } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import StudentLayout from '../../components/student/StudentLayout';
import { supabase } from '../../lib/supabase';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/svg+xml"];
const ACCEPTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.svg'];

interface ProfileData {
  id?: string;
  fullName: string;
  email: string;
  phone: string;
  bio?: string;
  address?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  birthDate?: string;
  avatar: string;
  avatarFile?: FileList | null; // Pour gérer l'upload de fichier
  joinDate: string;
  totalCourses: number;
  completedCourses: number;
  averageProgress: number;
  totalBadges: number;
  studyHours: number;
  activeCourses: number;
}

const StudentProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profileData, setProfileData] = useState<ProfileData>({
    fullName: '',
    email: '',
    phone: '',
    bio: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    birthDate: '',
    avatar: '',
    joinDate: '',
    totalCourses: 0,
    completedCourses: 0,
    averageProgress: 0,
    totalBadges: 0,
    studyHours: 0,
    activeCourses: 0,
  });

  const [editData, setEditData] = useState<ProfileData>(profileData);

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    if (profile?.id) {
      loadEnrollments(profile.id, profile);
    }
  };

  const loadEnrollments = async (studentId: string, profile: any) => {
    if (!studentId || !tenant?.id) return;

    setIsLoading(true);
    try {
      const enrollmentsData = await getStudentEnrollments(studentId, tenant.id);
      setEnrollments(enrollmentsData);

      const totalCourses = enrollmentsData.length;
      const completedCourses = enrollmentsData.filter(e => e.status === 'completed').length;
      const activeCourses = enrollmentsData.filter(e => e.status === 'active').length;
      const averageProgress = totalCourses > 0 
        ? Math.round(enrollmentsData.reduce((sum, e) => sum + e.progress_percentage, 0) / totalCourses)
        : 0;

      // TODO: Implémenter le calcul réel pour totalBadges et studyHours
      const totalBadges = 0; 
      const studyHours = 0;

      setProfileData(prev => ({
        ...prev,
        fullName: profile.full_name || prev.fullName,
        email: profile.email || prev.email,
        phone: profile.phone || prev.phone,
        bio: profile.bio || prev.bio,
        address: profile.address || prev.address,
        city: profile.city || prev.city,
        country: profile.country || prev.country,
        postalCode: profile.postal_code || prev.postalCode,
        birthDate: profile.birth_date || prev.birthDate,
        avatar: profile.avatar_url || prev.avatar,
        joinDate: profile.created_at || prev.joinDate,
        totalCourses,
        completedCourses,
        activeCourses,
        averageProgress,
        totalBadges,
        studyHours,
      }));

      setEditData(prev => ({
        ...prev,
        fullName: profile.full_name || prev.fullName,
        email: profile.email || prev.email,
        phone: profile.phone || prev.phone,
        bio: profile.bio || prev.bio,
        address: profile.address || prev.address,
        city: profile.city || prev.city,
        country: profile.country || prev.country,
        postalCode: profile.postal_code || prev.postalCode,
        birthDate: profile.birth_date || prev.birthDate,
        avatar: profile.avatar_url || prev.avatar,
        joinDate: profile.created_at || prev.joinDate,
      }));

    } catch (error) {
      console.error('Erreur lors du chargement des inscriptions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger vos cours',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };



  const handleSaveProfile = async () => {
    if (!studentProfile?.id) {
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder le profil: ID étudiant manquant.',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      let avatarUrl = editData.avatar;

      // Gérer l'upload de l'avatar si un nouveau fichier est sélectionné
      if (editData.avatarFile && editData.avatarFile.length > 0 && editData.avatarFile[0] instanceof File) {
        const file = editData.avatarFile[0];
        const fileExt = file.name.split('.').pop();
        const fileName = `avatar_${studentProfile.id}_${Date.now()}.${fileExt}`;
        const filePath = `avatars/${studentProfile.id}/${fileName}`;
        
        // Supprimer l'ancienne image si elle existe et si elle est dans notre bucket
        if (avatarUrl && avatarUrl.includes('storage/v1')) { // Vérifier si c'est une URL Supabase
          try {
            // Extraire le chemin du fichier à partir de l'URL
            const pathParts = avatarUrl.split('avatars/')[1]; // Assurez-vous que le bucket est 'avatars'
            if (pathParts) {
              await supabase.storage
                .from('avatars')
                .remove([decodeURIComponent(pathParts)]);
            }
          } catch (error) {
            console.error("Erreur lors de la suppression de l'ancien avatar:", error);
          }
        }
        
        // Téléverser le nouveau fichier
        const { error: uploadError } = await supabase.storage
          .from('avatars') // Assurez-vous que c'est le bon bucket
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) throw uploadError;

        // Obtenir l'URL publique
        const { data: publicUrlData } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        avatarUrl = publicUrlData.publicUrl;
      }

      const updatedProfile = await createOrUpdateStudentProfile({
        id: studentProfile.id,
        tenant_id: tenant?.id,
        domain: studentProfile.domain,
        email: editData.email,
        full_name: editData.fullName,
        phone: editData.phone,
        bio: editData.bio,
        address: editData.address,
        city: editData.city,
        country: editData.country,
        postal_code: editData.postalCode,
        birth_date: editData.birthDate || null,
        avatar_url: avatarUrl,
      });

      if (updatedProfile) {
        // Mapper ElearningStudentProfile vers ProfileData pour la mise à jour de l'état local
        const mappedProfileData: ProfileData = {
          fullName: updatedProfile.full_name || '',
          email: updatedProfile.email || '',
          phone: updatedProfile.phone || '',
          bio: updatedProfile.bio || '',
          address: updatedProfile.address || '',
          city: updatedProfile.city || '',
          country: updatedProfile.country || '',
          postalCode: updatedProfile.postal_code || '',
          birthDate: updatedProfile.birth_date || '',
          avatar: updatedProfile.avatar_url || '',
          joinDate: updatedProfile.created_at || '',
          totalCourses: profileData.totalCourses,
          completedCourses: profileData.completedCourses,
          averageProgress: profileData.averageProgress,
          totalBadges: profileData.totalBadges,
          studyHours: profileData.studyHours,
          activeCourses: profileData.activeCourses,
        };
        setProfileData(mappedProfileData);
        setStudentProfile(updatedProfile); // Mettre à jour le profil étudiant dans l'état global
        toast({
          title: 'Profil mis à jour',
          description: 'Vos informations ont été sauvegardées avec succès.'
        });
        setIsEditing(false);
      } else {
        throw new Error('La mise à jour du profil a échoué.');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du profil:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder le profil. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData(profileData);
    setIsEditing(false);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    let file: File | null = null;
    
    if ('dataTransfer' in e) {
      // Gestion du glisser-déposer
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        file = e.dataTransfer.files[0];
      }
    } else if (e.target.files && e.target.files.length > 0) {
      // Gestion du clic sur l'input file
      file = e.target.files[0];
    }
    
    if (file) {
      // Vérification du type de fichier
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ACCEPTED_IMAGE_EXTENSIONS.includes(fileExtension || '')) {
        toast({
          title: 'Format non supporté',
          description: 'Veuillez utiliser un fichier image (JPEG, JPG, PNG, WEBP ou SVG)',
          variant: 'destructive',
        });
        return;
      }
      
      // Vérification de la taille du fichier
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'Fichier trop volumineux',
          description: 'La taille maximale autorisée est de 5 Mo',
          variant: 'destructive',
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        // Création d'un DataTransfer pour simuler un FileList
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file as File);
        // Mise à jour de la valeur du champ de formulaire
        setEditData(prev => ({ ...prev, avatarFile: dataTransfer.files }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      // Supprimer l'image du stockage si elle existe
      if (imagePreview && imagePreview.startsWith('http')) {
        const fileName = imagePreview.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('avatars') // Assurez-vous que c'est le bon bucket
            .remove([fileName]);
        }
      }

      // Mettre à jour l'état local
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Mettre à jour le formulaire
      setEditData(prev => ({ ...prev, avatar: '', avatarFile: null }));
      
      // Mettre à jour la base de données si c'est une modification
      if (profileData.id) { // Supposons que profileData.id est l'ID du profil
        await supabase
          .from('elearn_customer_profiles')
          .update({ 
            avatar_url: null,
            updated_at: new Date().toISOString() 
          })
          .eq('id', profileData.id);
      }
      
      toast({
        title: 'Image supprimée',
        description: "L'image a été supprimée avec succès.",
      });
    } catch (error) {
      console.error("Erreur lors de la suppression de l'image:", error);
      toast({
        title: 'Erreur',
        description: "Une erreur est survenue lors de la suppression de l'image.",
        variant: 'destructive',
      });
    }
  };



  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'N/A'; // Retourne 'N/A' si la date est invalide
    }
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const completionRate = Math.round((profileData.completedCourses / profileData.totalCourses) * 100);



  return (
    <StudentLayout
      title="Mon Profil"
      description="Gérez vos informations personnelles et préférences"
      onStudentProfileLoaded={handleStudentProfileLoaded}
      headerContent={
        <Button
          onClick={() => {
            if (isEditing) {
              handleSaveProfile();
            } else {
              setIsEditing(true);
              setEditData(profileData);
            }
          }}
          className="flex items-center space-x-2"
        >
          {isEditing ? (
            <>
              <Save className="h-4 w-4" />
              <span>Sauvegarder</span>
            </>
          ) : (
            <>
              <Edit className="h-4 w-4" />
              <span>Modifier</span>
            </>
          )}
        </Button>
      }
    >
      <SEOMetaTags 
        title="Mon Profil - E-Learning"
        description="Gérez vos informations personnelles, préférences et paramètres de compte."
        keywords="profil, compte, informations personnelles, préférences, e-learning"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-1">

        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          {/* En-tête du profil */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="relative">
                      <Avatar className="h-24 w-24 mx-auto sm:mx-0">
                        <AvatarImage src={isEditing && imagePreview ? imagePreview : profileData.avatar} />
                        <AvatarFallback className="text-2xl">
                          {profileData.fullName ? profileData.fullName.split(' ')[0][0] : ''}
                          {profileData.fullName.split(' ').length > 1 ? profileData.fullName.split(' ')[1][0] : ''}
                        </AvatarFallback>
                      </Avatar>
                      {isEditing && (
                        <div className="absolute -bottom-2 -right-2 flex items-center space-x-2">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageChange}
                            className="hidden"
                            accept="image/jpeg,image/png,image/webp,image/svg+xml"
                          />
                          <Button
                            size="sm"
                            className="rounded-full p-2"
                            onClick={() => fileInputRef.current?.click()}
                            type="button"
                          >
                            <Camera className="h-4 w-4" />
                          </Button>
                          {imagePreview && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={removeImage}
                              className="text-red-400 hover:bg-red-400/20"
                            >
                              <X className="h-5 w-5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 text-center sm:text-left">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 space-y-2 sm:space-y-0">
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {profileData.fullName}
                          </h2>
                          <p className="text-gray-600 dark:text-gray-400">
                            Membre depuis {profileData.joinDate ? formatDate(profileData.joinDate) : 'N/A'}
                          </p>
                        </div>
                        
                        <Button
                          onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
                          className="flex items-center space-x-2"
                        >
                          {isEditing ? (
                            <> 
                              <Save className="h-4 w-4" />
                              <span>Sauvegarder</span>
                            </>
                          ) : (
                            <>
                              <Edit className="h-4 w-4" />
                              <span>Modifier</span>
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {/* Statistiques */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 sm:mt-0">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{profileData.totalCourses}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Cours</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{profileData.completedCourses}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Terminés</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-600">{profileData.activeCourses}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Cours Actifs</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">{profileData.averageProgress}%</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Progression Moyenne</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Informations personnelles */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <User className="h-5 w-5" />
                    <span>Informations personnelles</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Noms et Prénoms
                    </label>
                    <Input
                      value={isEditing ? editData.fullName : profileData.fullName}
                      onChange={(e) => setEditData(prev => ({ ...prev, fullName: e.target.value }))}
                      disabled={!isEditing}
                      className={!isEditing ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        type="email"
                        value={isEditing ? editData.email : profileData.email}
                        onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                        disabled={!isEditing}
                        className={`pl-10 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}`}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Téléphone
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        value={isEditing ? editData.phone : profileData.phone}
                        onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                        disabled={!isEditing}
                        className={`pl-10 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}`}
                      />
                    </div>
                  </div>
                  

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Adresse
                    </label>
                    <Input
                      value={isEditing ? editData.address : profileData.address}
                      onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))}
                      disabled={!isEditing}
                      className={!isEditing ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ville
                      </label>
                      <Input
                        value={isEditing ? editData.city : profileData.city}
                        onChange={(e) => setEditData(prev => ({ ...prev, city: e.target.value }))}
                        disabled={!isEditing}
                        className={!isEditing ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Pays
                      </label>
                      <Input
                        value={isEditing ? editData.country : profileData.country}
                        onChange={(e) => setEditData(prev => ({ ...prev, country: e.target.value }))}
                        disabled={!isEditing}
                        className={!isEditing ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Code Postal
                    </label>
                    <Input
                      value={isEditing ? editData.postalCode : profileData.postalCode}
                      onChange={(e) => setEditData(prev => ({ ...prev, postalCode: e.target.value }))}
                      disabled={!isEditing}
                      className={!isEditing ? 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}
                    />
                  </div>
                  

                  
                  {isEditing && (
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full">
                      <Button onClick={handleSaveProfile} className="w-full sm:w-auto">
                        <Save className="h-4 w-4 mr-2" />
                        Sauvegarder
                      </Button>
                      <Button variant="outline" onClick={handleCancelEdit} className="w-full sm:w-auto">
                        Annuler
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
    </StudentLayout>
  );
};

export default StudentProfilePage;
